import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSession } from '@/contexts/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import { useOptionalVmClient } from '@/api/context';
import { useAnonSessionStore } from '@/stores/anonSession';
import { Urls } from '@/lib/navigation';
import type { AnonPromptConfig } from '@/components/ChatInput/types';

interface AnonHome {
  onHome: () => void;
  anonPrompt: AnonPromptConfig;
}

export const useAnonHome = (): AnonHome => {
  const { session } = useAppSession();
  const { chat } = session;
  const auth = useAuth();
  const navigate = useNavigate();
  const client = useOptionalVmClient();
  const clearSavedAnonSession = useAnonSessionStore((s) => s.setSessionId);

  const [showPrompt, setShowPrompt] = useState(false);

  const onHome = useCallback(() => {
    if (!auth.isAuthenticated && chat.activeChatId) {
      setShowPrompt(true);
      return;
    }
    navigate(Urls.home());
  }, [auth.isAuthenticated, chat.activeChatId, navigate]);

  const confirmAnonNewAgent = useCallback(() => {
    const previousSessionId = chat.activeChatId;
    setShowPrompt(false);
    if (previousSessionId) {
      const prev = session.chatHistory.sessions.find((s) => s.id === previousSessionId);
      if (!prev?.is_example) {
        chat.removeChat(previousSessionId);
        void client?.deleteSession(previousSessionId).catch(() => {});
      }
    }
    clearSavedAnonSession(null);
    navigate(Urls.home());
  }, [chat, client, session.chatHistory.sessions, clearSavedAnonSession, navigate]);

  return {
    onHome,
    anonPrompt: {
      visible: showPrompt,
      onSignIn: auth.signIn,
      onContinue: confirmAnonNewAgent,
      onCancel: () => setShowPrompt(false),
    },
  };
};
